// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages } from "react-intl";
import { Button } from "react-bootstrap";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionTypeSignUp } from "actions";
import { DataState, UserStateAWSError, ForegroundPanel } from "types";
import { tabClickHandler, isValidEmail, MIN_PASSWORD_LENGTH } from "guiutils";
import { isSet } from "shared/utils/utils";
import LoginErrorStatus from "./LoginErrorStatus";
import InputField, { eInputFieldType } from "view/comp/InputField";

declare const IS_CLOUD: boolean;

interface IUserSignUpProps {
  signUpDataState: DataState;
  userEmail: string | undefined;
  signUpErrorFeedback: UserStateAWSError | undefined;
  willSignUp: boolean;
}

const mapStateToProps = (state: IState) => {
  const props: IUserSignUpProps = {
    signUpDataState: state.scheduler.loginDataState,
    userEmail: state.gui.login.email,
    willSignUp: state.gui.foregroundPanel !== ForegroundPanel.SIGNUP_CONFIRM,
    signUpErrorFeedback: state.gui.login.userStateAWSFeedback,
  };
  return props;
};

interface IUserSignUpState {
  username: string;
  password: string;
  verifyPassword: string;
  name: string;
  company: string;
  confirmationCode: string;
  showMismatchError: boolean;
  showUsernameError: boolean;
  showConfirmationCodeError: boolean;
}

interface IUserSignUpDispatch {
  onCancel: (username: string) => void;
  onSignUpClick: (state: IUserSignUpState) => void;
  onSignUpResendClick: (state: IUserSignUpState) => void;
  onSignUpConfirmClick: (state: IUserSignUpState) => void;
  onShowSignUpConfirmClick: (username: string) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IUserSignUpDispatch = {
    onCancel: (username: string) => {
      dispatch(raiseAction(ActionType.ACT_BACK_TO_LOGIN, username));
    },
    onSignUpClick: (state: IUserSignUpState) => {
      const payload: IActionTypeSignUp = {
        username: state.username,
        password: state.password,
        company: state.company,
        realName: state.name,
        keepErrorState: false,
      };
      dispatch(raiseAction(ActionType.ACT_SIGNUP_REQ, payload));
    },
    onSignUpResendClick: (state: IUserSignUpState) => {
      const payload: IActionTypeSignUp = {
        username: state.username,
        password: "",
        keepErrorState: false,
      };
      dispatch(raiseAction(ActionType.ACT_SIGNUP_RESEND_REQ, payload));
    },
    onSignUpConfirmClick: (state: IUserSignUpState) => {
      const payload: IActionTypeSignUp = {
        username: state.username,
        password: state.password,
        code: state.confirmationCode,
        keepErrorState: false,
      };
      dispatch(raiseAction(ActionType.ACT_SIGNUP_CONFIRM_REQ, payload));
    },
    onShowSignUpConfirmClick: (username: string) => {
      const payload: IActionTypeSignUp = {
        username,
        password: "",
        keepErrorState: false,
      };
      dispatch(raiseAction(ActionType.ACT_SIGNUP_CONFIRM, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  emailPlaceholder: { id: "e.g. jane@doe.com" },
  passwordPlaceholder: { id: "min. 8 characters/digits/symbols" },
  fullNamePlaceholder: { id: "Full name" },
});

class UserSignUpComponent extends React.Component<
  IUserSignUpProps & IUserSignUpDispatch,
  IUserSignUpState
> {
  state: IUserSignUpState = {
    username: IS_CLOUD ? (this.props.userEmail ?? "") : "operator",
    password: "",
    verifyPassword: "",
    name: "",
    company: "",
    confirmationCode: "",
    showMismatchError: false,
    showUsernameError: false,
    showConfirmationCodeError: false,
  };

  public render() {
    const validationAlert = this.renderValidationAlert();
    return (
      <>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <table className="w-100">
            <tbody>
              {this.props.willSignUp && (
                <>
                  <tr className="loginLabel">
                    <td>
                      <label htmlFor="name">
                        <FormattedMessage id="Name" />
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <InputField
                        inputType={eInputFieldType.Text}
                        name="name"
                        autoComplete="name"
                        value={this.state.name}
                        dataState={this.props.signUpDataState}
                        placeholder={messages.fullNamePlaceholder}
                        autoFocus
                        onChange={(event) => this.setState({ name: event?.currentTarget.value })}
                      />
                    </td>
                  </tr>
                </>
              )}
              {(
                this.props.willSignUp || !isSet(this.props.userEmail) || this.props.userEmail === ""
              ) ?
                <>
                  <tr className="loginLabel">
                    <td>
                      <label htmlFor="username">
                        <FormattedMessage id="Email" />
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <InputField
                        inputType={eInputFieldType.Text}
                        name="username"
                        autoComplete="username"
                        value={this.state.username}
                        dataState={this.props.signUpDataState}
                        errorText={
                          (
                            (this.props.signUpDataState === DataState.SAVE_FAIL &&
                              this.props.signUpErrorFeedback ===
                                UserStateAWSError.USERNAME_MISSING) ||
                            (this.state.showUsernameError && !this.isUsernameValid())
                          ) ?
                            "Check your Email Address"
                          : undefined
                        }
                        placeholder={messages.emailPlaceholder}
                        onChange={(event) =>
                          this.setState({ username: event?.currentTarget.value })
                        }
                      />
                    </td>
                  </tr>
                </>
              : <>
                  <tr>
                    <td>
                      <FormattedMessage id="Please enter the code you received via email" />
                      <b>{` ${this.state.username} `}</b>
                      {!isSet(this.state.password) || this.state.password === "" ?
                        <FormattedMessage id="together with your password..." />
                      : <FormattedMessage id="below..." />}
                    </td>
                  </tr>
                  {IS_CLOUD &&
                    this.props.signUpDataState !== DataState.SAVE_REQ &&
                    isSet(this.props.userEmail) &&
                    this.props.userEmail !== "" && (
                      <tr>
                        <td>
                          <div className="loginOptions">
                            <div className="link"></div>
                            <span
                              tabIndex={0}
                              onKeyDown={tabClickHandler}
                              className="text-link ms-3"
                              onClick={() => {
                                this.setState({ username: "" });
                                this.props.onShowSignUpConfirmClick("");
                              }}
                            >
                              <FormattedMessage id="Not your email?" />
                            </span>
                          </div>
                          <div className="mb-4"></div>
                        </td>
                      </tr>
                    )}
                </>
              }
              {this.props.willSignUp ?
                <>
                  <tr className="loginLabel">
                    <td>
                      <label htmlFor="newPassword">
                        <FormattedMessage id="New Password" />
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <InputField
                        inputType={eInputFieldType.Password}
                        name="newPassword"
                        autoComplete="new-password"
                        value={this.state.password}
                        dataState={this.props.signUpDataState}
                        errorText={
                          (
                            this.isPasswordTooShort() ||
                            (this.state.showMismatchError &&
                              (this.state.password === "" ||
                                this.state.password !== this.state.verifyPassword)) ||
                            (this.props.signUpDataState === DataState.SAVE_FAIL &&
                              (this.props.signUpErrorFeedback ===
                                UserStateAWSError.PASSWORD_INVALID ||
                                this.props.signUpErrorFeedback ===
                                  UserStateAWSError.PASSWORD_MISSING))
                          ) ?
                            "Check your Password"
                          : undefined
                        }
                        placeholder={messages.passwordPlaceholder}
                        onChange={(event) =>
                          this.setState({ password: event?.currentTarget.value })
                        }
                      />
                    </td>
                  </tr>
                  <tr className="loginLabel">
                    <td>
                      <label htmlFor="confirmPassword">
                        <FormattedMessage id="Verify new Password" />
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <InputField
                        inputType={eInputFieldType.Password}
                        name="confirmPassword"
                        autoComplete="new-password"
                        value={this.state.verifyPassword}
                        dataState={this.props.signUpDataState}
                        errorText={
                          (
                            this.isPasswordTooShort() ||
                            (this.state.showMismatchError &&
                              (this.state.password === "" ||
                                this.state.password !== this.state.verifyPassword)) ||
                            (this.props.signUpDataState === DataState.SAVE_FAIL &&
                              (this.props.signUpErrorFeedback ===
                                UserStateAWSError.PASSWORD_INVALID ||
                                this.props.signUpErrorFeedback ===
                                  UserStateAWSError.PASSWORD_MISSING))
                          ) ?
                            "Check your Password"
                          : undefined
                        }
                        placeholder={messages.passwordPlaceholder}
                        onChange={(event) =>
                          this.setState({ verifyPassword: event?.currentTarget.value })
                        }
                      />
                    </td>
                  </tr>
                </>
              : <>
                  <tr className="loginLabel">
                    <td>
                      <label htmlFor="confirmationCode">
                        <FormattedMessage id="Confirmation Code" />
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <InputField
                        inputType={eInputFieldType.Text}
                        name="confirmationCode"
                        autoComplete="one-time-code"
                        value={this.state.confirmationCode}
                        dataState={this.props.signUpDataState}
                        errorText={
                          this.props.signUpDataState === DataState.SAVE_FAIL ? " "
                          : (
                            this.state.showConfirmationCodeError &&
                            this.state.confirmationCode === ""
                          ) ?
                            "ConfirmationCode needed"
                          : undefined
                        }
                        onChange={(event) =>
                          this.setState({ confirmationCode: event?.currentTarget.value })
                        }
                      />
                    </td>
                  </tr>
                  <tr className="loginLabel">
                    <td>
                      <label htmlFor="password">
                        <FormattedMessage id="Password" />
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <InputField
                        inputType={eInputFieldType.Password}
                        name="password"
                        autoComplete="new-password"
                        value={this.state.password}
                        dataState={this.props.signUpDataState}
                        errorText={
                          (
                            (this.props.signUpDataState === DataState.SAVE_FAIL &&
                              (this.props.signUpErrorFeedback === UserStateAWSError.LOGIN_FAILED ||
                                this.props.signUpErrorFeedback ===
                                  UserStateAWSError.PASSWORD_MISSING)) ||
                            (this.state.showMismatchError && !this.isPasswordValid())
                          ) ?
                            "Check your Password"
                          : undefined
                        }
                        onChange={(event) =>
                          this.setState({ password: event?.currentTarget.value })
                        }
                      />
                    </td>
                  </tr>
                </>
              }
              {IS_CLOUD &&
                this.props.signUpDataState !== DataState.SAVE_REQ &&
                this.props.willSignUp === false && (
                  <tr>
                    <td>
                      <div className="loginOptions">
                        <div className="link" style={{ marginTop: 11 }}></div>
                        <span
                          tabIndex={0}
                          onKeyDown={tabClickHandler}
                          className="text-link ms-3"
                          onClick={() => {
                            this.setState({ confirmationCode: "" });
                            this.props.onSignUpResendClick(this.state);
                          }}
                        >
                          <FormattedMessage id="Resend Code..." />
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
          <div className="mt-3">
            <div className="button-row">
              <Button
                type="button"
                variant="secondary"
                onClick={(event) => this.props.onCancel(this.state.username)}
                disabled={this.props.signUpDataState === DataState.SAVE_REQ}
              >
                <FormattedMessage id="Cancel" />
              </Button>
              <Button
                id="perform-signup"
                type="submit"
                // main action here, unlike the secondary entry button in SignupEntry.tsx
                variant="primary"
                onClick={() => {
                  this.props.willSignUp ? this.signUp() : this.signUpConfirmation();
                }}
                disabled={this.props.signUpDataState === DataState.SAVE_REQ}
              >
                {this.props.willSignUp ?
                  <FormattedMessage id="SignUp" />
                : <FormattedMessage id="Confirm" />}
              </Button>
            </div>
            {validationAlert ?
              <div id="signup-validation-alert">{validationAlert}</div>
            : this.props.signUpDataState === DataState.SAVE_FAIL && <LoginErrorStatus />}
          </div>
        </form>
      </>
    );
  }

  private renderValidationAlert() {
    if (this.props.willSignUp) {
      if (this.state.showUsernameError && !this.isUsernameValid()) {
        return (
          <div className="alert alert-danger mt-3">
            <FormattedMessage id="Please enter a valid email address." />
          </div>
        );
      }
      if (this.isPasswordTooShort()) {
        return (
          <div className="alert alert-danger mt-3">
            <FormattedMessage id="Your password must be at least 8 characters long." />
          </div>
        );
      }
      if (this.state.showMismatchError && this.state.password === "") {
        return (
          <div className="alert alert-danger mt-3">
            <FormattedMessage id="Please enter a password." />
          </div>
        );
      }
      if (this.state.showMismatchError && this.state.password !== this.state.verifyPassword) {
        return (
          <div className="alert alert-danger mt-3">
            <FormattedMessage id="The two fields for the new password do not match." />
          </div>
        );
      }
      return null;
    }

    // confirmation mode
    if (this.state.showConfirmationCodeError && !this.isConfirmationCodeValid()) {
      return (
        <div className="alert alert-danger mt-3">
          <FormattedMessage id="Please enter the verification code." />
        </div>
      );
    }
    if (this.state.showMismatchError && this.state.password === "") {
      return (
        <div className="alert alert-danger mt-3">
          <FormattedMessage id="Please enter a password." />
        </div>
      );
    }
    if (this.state.showMismatchError && !this.isPasswordValid()) {
      return (
        <div className="alert alert-danger mt-3">
          <FormattedMessage id="Your password must be at least 8 characters long." />
        </div>
      );
    }
    return null;
  }

  private signUp() {
    if (this.isUsernameValid() && this.isPasswordValid()) {
      this.props.onSignUpClick(this.state);
    } else {
      this.setState({ showUsernameError: true, showMismatchError: true });
    }
  }

  signUpConfirmation() {
    if (this.isUsernameValid() && this.isPasswordValid() && this.isConfirmationCodeValid()) {
      this.props.onSignUpConfirmClick(this.state);
    } else {
      this.setState({
        showUsernameError: true,
        showMismatchError: true,
        showConfirmationCodeError: true,
      });
    }
  }

  private isUsernameValid() {
    if (IS_CLOUD) {
      return isValidEmail(this.state.username);
    }
    return true;
  }

  private isPasswordValid() {
    if (IS_CLOUD) {
      return (
        this.state.password !== "" &&
        this.state.password.length >= MIN_PASSWORD_LENGTH &&
        (this.props.willSignUp === false || this.state.password === this.state.verifyPassword)
      );
    }
    return true;
  }

  private isConfirmationCodeValid() {
    return this.state.confirmationCode !== "";
  }

  private isPasswordTooShort() {
    return (
      this.state.password.length > 0 &&
      this.state.password === this.state.verifyPassword &&
      this.state.password.length < MIN_PASSWORD_LENGTH
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserSignUpComponent);
